import React, { useState } from 'react';
import { makeStyles } from '../library';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    IconButton,
    Tooltip,
    Typography,
    SimpleDraggable as Draggable,
    Box,
    Stepper,
    Step,
    StepLabel,
    Button as MuiButton
} from '../library';

import * as icons from '../icons';

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        '& > *': {
            marginLeft: theme.spacing(1)
        }
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[20],
        padding: theme.spacing(2, 4, 3)
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.primary.light,
        borderTop: `1px solid ${theme.palette.primary[200] || theme.palette.primary.main}`
    },
    stepContent: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        minHeight: '200px'
    },
    navButtons: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1)
    }
}));

const PaperComponent = props => {
    return (
        <Draggable
            handle='#draggable-dialog-title'
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
};

/**
 * WizardModal component that displays a modal with step-by-step navigation
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.close - Function to close the modal
 * @param {string} props.title - Title of the modal
 * @param {Array} props.components - Array of components to display as steps
 * @param {Array} props.stepLabels - Array of labels for the steps
 * @param {Function} props.onFinish - Function to call when the wizard is finished
 * @param {Function} [props.getNextStep] - Controls jump to next step (currentStep) => nextStepIndex
 * @param {Function} [props.getPrevStep] - Controls jump to previous step (currentStep) => prevStepIndex
 * @param {Function} [props.isStepVisible] - Hides steps in stepper visual (stepIndex) => boolean
 * @param {Function} [props.canProceed] - Blocks advance if validation fails (activeStep) => boolean
 * @param {Object} props.rest - Additional props to pass to the Dialog component
 */
const WizardModal = props => {
    const {
        open,
        close,
        title,
        components = [],
        stepLabels = [],
        onFinish,
        getNextStep,
        getPrevStep,
        isStepVisible,
        canProceed,
        ...rest
    } = props;

    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const maxSteps = components.length;

    // If no components are provided, don't render anything
    if (!open || components.length === 0) return null;

    // Generate step labels if not provided
    const allLabels =
        stepLabels.length === components.length
            ? stepLabels
            : components.map((_, index) => `Paso ${index + 1}`);

    // Build visible steps for the stepper display
    const visibleSteps = allLabels
        .map((label, index) => ({ label, index }))
        .filter(item => !isStepVisible || isStepVisible(item.index));

    // Map activeStep to the visual stepper index
    const visualActiveStep = visibleSteps.findIndex(
        item => item.index === activeStep
    );

    const handleNext = () => {
        if (canProceed && !canProceed(activeStep)) return;
        if (getNextStep) {
            setActiveStep(getNextStep(activeStep));
        } else {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (getPrevStep) {
            setActiveStep(getPrevStep(activeStep));
        } else {
            setActiveStep(prev => prev - 1);
        }
    };

    const handleFinish = () => {
        if (canProceed && !canProceed(activeStep)) return;
        if (onFinish) {
            onFinish();
        }
    };

    const handleCancel = () => {
        setActiveStep(0);
        close();
    };

    return (
        <Dialog
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            maxWidth='md'
            fullWidth
            {...rest}
        >
            <DialogTitle
                style={{ cursor: 'move', padding: '8px 16px' }}
                id='draggable-dialog-title'
            >
                <div className={classes.wrapper}>
                    <Tooltip title={'Cerrar'}>
                        <IconButton size='small' onClick={handleCancel}>
                            <icons.CloseIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant='h6'>{title}</Typography>
                </div>
            </DialogTitle>
            <DialogContent className={classes.paper}>
                <Stepper
                    sx={{ mt: 2 }}
                    activeStep={
                        visualActiveStep >= 0 ? visualActiveStep : activeStep
                    }
                >
                    {visibleSteps.map((item, idx) => (
                        <Step key={idx}>
                            <StepLabel>{item.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Box className={classes.stepContent}>
                    {components[activeStep]}
                </Box>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <MuiButton
                    variant='contained'
                    size='small'
                    color='error'
                    startIcon={<icons.CancelIcon />}
                    onClick={handleCancel}
                >
                    Cancelar
                </MuiButton>
                <Box className={classes.navButtons}>
                    <MuiButton
                        variant='outlined'
                        size='small'
                        startIcon={<icons.PreviousIcon />}
                        onClick={handleBack}
                        disabled={activeStep === 0}
                    >
                        Anterior
                    </MuiButton>
                    {activeStep === maxSteps - 1 ? (
                        <MuiButton
                            variant='contained'
                            size='small'
                            color='primary'
                            endIcon={<icons.OkIcon />}
                            onClick={handleFinish}
                        >
                            Finalizar
                        </MuiButton>
                    ) : (
                        <MuiButton
                            variant='contained'
                            size='small'
                            color='primary'
                            endIcon={<icons.NextIcon />}
                            onClick={handleNext}
                        >
                            Siguiente
                        </MuiButton>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default WizardModal;
